import { useEffect, useRef, useState } from 'react'
import { Input, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { ChromePicker } from 'react-color'
import { ProfTag } from '@/models/profTag.ts'

const DEFAULT_TAG_COLOR = '#7EB6E6'

export function useTagEditDialog() {
  const modalRef = useRef<HTMLDialogElement>(null)
  const [tag, setTag] = useState<ProfTag | null>(null)

  function open(t: ProfTag) {
    setTag(t)
    setTimeout(() => modalRef.current?.showModal(), 0)
  }

  function close() {
    modalRef.current?.close()
  }

  return { modalRef, tag, open, close }
}

interface TagEditDialogProps {
  modalRef: React.RefObject<HTMLDialogElement | null>
  tag: ProfTag | null
  onSave: (tagId: string, data: { name: string, color: string }) => Promise<void>
  onClose?: () => void
}

export default function TagEditDialog({ modalRef, tag, onSave, onClose }: TagEditDialogProps) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Sync local state when tag changes
  useEffect(() => {
    if (tag) {
      setName(tag.name)
      setColor(tag.color)
    }
  }, [tag])

  async function handleSave() {
    if (!tag || !name.trim() || isSaving) return
    setIsSaving(true)
    try {
      await onSave(tag.id, { name: name.trim(), color })
      modalRef.current?.close()
      onClose?.()
    } finally {
      setIsSaving(false)
    }
  }

  function handleClose() {
    modalRef.current?.close()
    onClose?.()
  }

  if (!tag) return null

  return (
     // @ts-ignore ref type mismatch with DaisyUI Modal
    <Modal ref={modalRef} className="bg-neutral max-w-xs">
      <Modal.Header>
        <span className="text-lg font-bold">Tag bearbeiten</span>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <label className="form-control w-full">
            <div className="label">
              <span className="label-text text-sm">Name</span>
            </div>
            <Input
              size="sm"
              className="w-full"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={64}
              onKeyDown={e => { if (e.key === 'Enter') handleSave() }}
              autoFocus
            />
          </label>

          <div>
            <div className="label">
              <span className="label-text text-sm">Farbe</span>
            </div>
            <div className="card bg-secondary">
              <div className="card-body p-3 items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full border-2 border-neutral"
                  style={{ backgroundColor: color ? `#${color}` : DEFAULT_TAG_COLOR }}
                />
                <ChromePicker
                  disableAlpha
                  // @ts-ignore
                  color={color ? `#${color}` : DEFAULT_TAG_COLOR}
                  onChange={(c) => setColor(c.hex.replace('#', ''))}
                />
                {color && (
                  <Button size="sm" color="ghost" onClick={() => setColor('')}>
                    Farbe entfernen
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" color="ghost" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button size="sm" color="primary" onClick={handleSave} loading={isSaving} disabled={isSaving || !name.trim()}>
              Speichern
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
